import { useState, useEffect, useCallback, useRef } from "react";

import { DataTable } from "primereact/datatable";
import type { DataTableValueArray } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Skeleton } from "primereact/skeleton";
import { FilterMatchMode } from "primereact/api";

import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { ROWS_PER_PAGE_OPTIONS } from "sj-ui-toolkit/src/constants/tableRowsCounts";
import { useApiPagination, useToast, useDebounce } from "sj-ui-toolkit/src/hooks";
import { humanDateWithoutTime } from "sj-ui-toolkit/src/utils/dates";
import { getCurrentSelectedTenant } from "sj-ui-toolkit/src/utils";

import { subDays, subMonths, subYears } from "date-fns";

import UsageSummary from "@/components/UsageSummary";
import AccountBalance from "@/components/AccountBalance";

import { loadUsageReport, loadCustomerTransactions, exportUsageReportCSV } from "@/api/reports";
import type { UsageResponse, CustomerTransaction } from "@/api/reports";
import { fetchSiteSettings } from "@/api/settings";
import { withCurrency } from "@/helpers/reports";
import { withSkeleton, dateBody } from "@/helpers/tableBody";
import { dropdownFilterElement } from "@/helpers/tableFilters";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DATE_RANGE_OPTIONS = [
  { label: "All Time", value: "0" },
  { label: "Last 24 hours", value: "last24hours" },
  { label: "Last Week", value: "lastWeek" },
  { label: "Last Month", value: "lastMonth" },
  { label: "Last Year", value: "lastYear" },
];

const TRANSACTION_TYPE_OPTIONS = [
  { label: "Deposit", value: "Deposit" },
  { label: "Spending", value: "Spending" },
];

type DateRangeValue = "0" | "last24hours" | "lastWeek" | "lastMonth" | "lastYear" | undefined;
type BillingTypeMode = "per_lesson" | "per_user" | null;

interface DateRange {
  from: Date;
  to: Date;
}

// ---------------------------------------------------------------------------
// Inline column templates (single-use, specific to this page)
// ---------------------------------------------------------------------------

const descriptionTemplate = (rowData: CustomerTransaction | null): JSX.Element | string => {
  if (rowData == null || !rowData.transactionType)
    return <Skeleton width="12rem" height="1.25rem" />;

  if (rowData.transactionType === "Deposit" && rowData.entity) {
    const { billingType, entity } = rowData;
    const userCount = entity.userCount;
    const expiration = entity.expiration;

    return (
      <div>
        <p>
          <span className="font-semibold">billing type:</span> {billingType?.replace(/_/g, " ")}
        </p>
        {userCount != null && (
          <p>
            <span className="font-semibold">user count:</span> {userCount} users
          </p>
        )}
        {expiration && (
          <p>
            <span className="font-semibold">expiration:</span>{" "}
            {new Date(expiration).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        )}
      </div>
    );
  }

  return rowData.description ?? "";
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const UsageReport = () => {
  useBreadcrumb({
    label: "Usage Report",
    route: AdminAppRoutes.routes.reporting.routes.usage.route,
    display: "none",
    resetTrail: true,
  });

  const { showToast } = useToast();
  const currentTenantName = getCurrentSelectedTenant();
  const dt = useRef<DataTable<DataTableValueArray>>(null);

  // Usage summary state
  const [usageData, setUsageData] = useState<UsageResponse>({});
  const [isUsageLoading, setIsUsageLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  // Billing mode state
  const [billingTypeMode, setBillingTypeMode] = useState<BillingTypeMode>(null);
  const [isSiteLoading, setIsSiteLoading] = useState(true);

  // ---------------------------------------------------------------------------
  // Server-side pagination (declared first so lazyState is available for
  // initializing globalFilterValue from persisted storage below)
  // ---------------------------------------------------------------------------

  const { data, totalRecords, lazyState, onPage, onFilter } = useApiPagination({
    storageKey: "usageReport",
    dataKey: "customerTransactions",
    loadData: async ({ page, perPage, filters }) => {
      const params = {
        page,
        per_page: perPage,
        query: (filters as Record<string, { value: string | null }>).global?.value ?? null,
        filters: (() => {
          const v = (filters as Record<string, { value: string | null }>).transactionType?.value;
          return v ? [v] : [];
        })(),
      };

      const response = await loadCustomerTransactions(params);

      const customerTransactions = (response.customerTransactions ?? []).map(t => ({
        ...t,
        formattedDate: humanDateWithoutTime(t.createdAt),
      }));

      return { ...response, customerTransactions };
    },
    calculateTotalRecords: (response, pageSize) => {
      const transactions = response.customerTransactions ?? [];
      const totalPages = response.totalPages ?? 1;

      if (transactions.length < pageSize) {
        return (totalPages - 1) * pageSize + transactions.length;
      }
      return totalPages * pageSize;
    },
  });

  // Filter state — globalFilterValue is lazy-initialized from persisted lazyState.filters
  // to survive page reloads, and kept in sync with onFilter via useEffect
  const [globalFilterValue, setGlobalFilterValue] = useState<string>(
    () => (lazyState.filters as Record<string, { value: string | null }>)?.global?.value ?? ""
  );
  const [dateRange, setDateRange] = useState<DateRange | null>(null);
  const [selectedDateRange, setSelectedDateRange] = useState<DateRangeValue>(undefined);

  // Keep dateRange in a ref for the export handler (stale-closure guard)
  const dateRangeRef = useRef<DateRange | null>(null);
  useEffect(() => {
    dateRangeRef.current = dateRange;
  });

  // Always-current snapshot of lazyState.filters — prevents stale closure in the
  // debounced search effect without adding unstable values to the dep array
  const filtersRef = useRef(lazyState.filters);
  useEffect(() => {
    filtersRef.current = lazyState.filters;
  });

  // ---------------------------------------------------------------------------
  // Debounced global search → drives useApiPagination filter
  // ---------------------------------------------------------------------------

  const debouncedSearch = useDebounce(globalFilterValue, 300);

  useEffect(() => {
    const currentFilters = (filtersRef.current ?? {}) as Record<string, Record<string, unknown>>;
    onFilter({
      ...lazyState,
      filters: {
        ...currentFilters,
        global: {
          ...(currentFilters.global ?? {}),
          value: debouncedSearch || null,
        },
      },
    });
  }, [debouncedSearch]);

  // ---------------------------------------------------------------------------
  // Usage summary + billing type
  // ---------------------------------------------------------------------------

  const fetchUsageData = useCallback(
    async (params: { from?: number; to?: number } = {}) => {
      setIsUsageLoading(true);
      try {
        const result = await loadUsageReport(params);
        setUsageData(result);
      } catch (error) {
        console.error("Failed to load usage data:", error);
        showToast({ severity: "error", summary: "Error", detail: "Failed to load usage data" });
      } finally {
        setIsUsageLoading(false);
      }
    },
    [showToast]
  );

  useEffect(() => {
    fetchUsageData();
  }, [currentTenantName, fetchUsageData]);

  useEffect(() => {
    const load = async () => {
      try {
        const site = await fetchSiteSettings();
        setBillingTypeMode((site?.billingTypeMode as BillingTypeMode) ?? null);
      } catch (error) {
        console.error("Failed to fetch site settings:", error);
      } finally {
        setIsSiteLoading(false);
      }
    };
    load();
  }, []);

  // ---------------------------------------------------------------------------
  // Date range
  // ---------------------------------------------------------------------------

  const handleDateRangeChange = useCallback(
    (from: Date, to: Date) => {
      setDateRange({ from, to });
      fetchUsageData({
        from: Math.floor(from.getTime() / 1000),
        to: Math.floor(to.getTime() / 1000),
      });
    },
    [fetchUsageData]
  );

  const handleDateRangeSelect = useCallback(
    (value: DateRangeValue) => {
      setSelectedDateRange(value);

      if (value === "0") {
        setDateRange(null);
        fetchUsageData();
        return;
      }

      const to = new Date();
      let from: Date;

      switch (value) {
        case "last24hours":
          from = subDays(to, 1);
          break;
        case "lastWeek":
          from = subDays(to, 7);
          break;
        case "lastMonth":
          from = subMonths(to, 1);
          break;
        case "lastYear":
          from = subYears(to, 1);
          break;
        default:
          return;
      }

      handleDateRangeChange(from, to);
    },
    [fetchUsageData, handleDateRangeChange]
  );

  // ---------------------------------------------------------------------------
  // CSV export (usage summary — reads dateRange via ref, no stale closure)
  // ---------------------------------------------------------------------------

  const handleExportCSV = useCallback(async () => {
    setIsExporting(true);
    try {
      const range = dateRangeRef.current;
      const params: { from?: number; to?: number } = {};
      if (range?.from && range?.to) {
        params.from = Math.floor(range.from.getTime() / 1000);
        params.to = Math.floor(range.to.getTime() / 1000);
      }
      await exportUsageReportCSV(params);
    } catch (error) {
      console.error("Failed to export usage data:", error);
      showToast({ severity: "error", summary: "Error", detail: "Failed to Export data" });
    } finally {
      setIsExporting(false);
    }
  }, [showToast]);

  // ---------------------------------------------------------------------------
  // Column body templates (memoized to prevent re-renders)
  // ---------------------------------------------------------------------------

  const typeTemplate = withSkeleton<CustomerTransaction>(row => row.transactionType ?? "", "4rem");

  const amountTemplate = withSkeleton<CustomerTransaction>(
    row => (row.amount != null ? withCurrency(row.amount) : ""),
    "4rem"
  );

  const dateTemplate = dateBody<CustomerTransaction>("createdAt");

  const balanceTemplate = withSkeleton<CustomerTransaction>(
    row => (row.currentBalance != null ? withCurrency(row.currentBalance) : ""),
    "4rem"
  );

  const transactionTypeFilterTemplate = dropdownFilterElement(
    TRANSACTION_TYPE_OPTIONS,
    "All types"
  );

  const tableFilters = {
    transactionType: { value: null as string | null, matchMode: FilterMatchMode.EQUALS },
    ...(lazyState.filters as Record<string, unknown>),
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <>
      <div className="mb-4">
        <h1>Usage Report</h1>
      </div>

      <div className="flex gap-8 items-center mb-4 mt-10">
        <Dropdown
          value={selectedDateRange}
          options={DATE_RANGE_OPTIONS}
          onChange={e => handleDateRangeSelect(e.value as DateRangeValue)}
          placeholder="Select date range"
          className="w-50"
        />
        <Button
          label="Export data"
          icon="pi pi-download"
          onClick={() => void handleExportCSV()}
          loading={isExporting}
          disabled={isExporting}
          data-testid="usage-export-button"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
        <UsageSummary
          levels={usageData.levels}
          totalConsumed={usageData.totalConsumed}
          isLoading={isUsageLoading}
        />
        <AccountBalance
          accountBalance={usageData.accountBalance}
          lessonPrice={usageData.lessonPrice}
          dojoActivityPrice={usageData.dojoActivityPrice}
          awarenessLessonPrice={usageData.awarenessLessonPrice}
          experimentPrice={usageData.experimentPrice}
          billingTypeMode={billingTypeMode}
          currentLicense={usageData.currentLicense}
          isLoading={isSiteLoading}
        />
      </div>

      <h3 className="atlas-h3 mb-6">Transactions</h3>

      <div className="flex gap-8 mb-4 items-center justify-between">
        <InputText
          value={globalFilterValue}
          onChange={e => setGlobalFilterValue(e.target.value)}
          placeholder="Search transactions..."
          aria-label="Search transactions"
          data-testid="usage-search-input"
          className="w-80"
        />
        <Button label="Export data" icon="pi pi-download" onClick={() => dt.current?.exportCSV()} />
      </div>

      <DataTable
        ref={dt}
        value={data as CustomerTransaction[]}
        lazy
        paginator
        aria-label="Usage report"
        data-testid="usage-report-table"
        first={lazyState.first}
        rows={lazyState.rows}
        totalRecords={totalRecords}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        onPage={onPage}
        onFilter={onFilter}
        filters={tableFilters}
        emptyMessage="No transactions found"
        className="p-datatable-sm"
        tableClassName="dt-responsive-text"
      >
        <Column
          field="transactionType"
          header="Type"
          body={typeTemplate}
          filter
          filterElement={transactionTypeFilterTemplate}
          showFilterMatchModes={false}
        />
        <Column field="description" header="Description" body={descriptionTemplate} />
        <Column field="amount" header="Amount" body={amountTemplate} />
        <Column field="formattedDate" header="Date (UTC)" body={dateTemplate} />
        <Column field="currentBalance" header="Balance" body={balanceTemplate} />
      </DataTable>
    </>
  );
};

export default UsageReport;
